import { ErrorBoundary } from "@/ui/error-boundary";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState, type ReactNode } from "react";
import { api, qs } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { usePrefsLoader } from "@/lib/prefs";
import { Link, match, navigate, setTitleSuffix, useLocation, usePageTitle } from "@/lib/router";
import { pickIcon, setFavicon, useBrand } from "@/lib/brand";
import { fmtDateTime } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import { useFeatures } from "@/lib/features";
import { Icon, Avatar } from "@/ui/icons";
import { ConfirmHost, Toaster } from "@/ui/feedback";
import { applyTheme, isDark, setDark, type OfficeTheme } from "@/ui/theme";
import { useMe, AccountPage } from "./AccountPage";
import { PanelPage } from "./PanelPage";
import { WorkflowPage } from "./WorkflowPage";
import { useProfile } from "@/lib/profile";
import { useUnitStore } from "@/lib/unit";
import { PeoplePage, PersonDetail } from "./PeoplePages";
import { CasesPage, CaseDetail } from "./CasePages";
import { ProcessesPage, ProcessDetail } from "./ProcessPages";
import { ActivitiesPage, ActivityDetailPage, NewActivityModal } from "./ActivityPages";
import { DeadlineFab, type CalcApply } from "@/ui/deadline-calc";
import { AgendaPage } from "./AgendaPage";
import { ControlPage } from "./ControlPage";
import { FinancePage } from "./FinancePage";
import { DocumentsPage } from "./DocumentsPage";
import { SettingsPage } from "./SettingsPage";
import { CommWorkspace } from "./CommWorkspace";
import { IndicatorsPage } from "./IndicatorsPage";
import { SocietyPage } from "./SocietyPage";
import { MuralPage } from "./MuralPage";
import { FeesPage } from "./FeesPage";
import { ChatPage } from "./ChatPage";
import { AssistantPage } from "./AssistantPage";
import { useLiveChat } from "@/lib/live";
import { ChatDock } from "@/ui/chat-dock";

const NOTIF_ICON: Record<string, string> = { movimentacao: "radar", email: "inbox", atribuicao: "check", revisao: "tick", devolucao: "alert", intimacao: "radar", fatal24h: "clock", mencao: "chat", lembrete: "pin" };
const NOTIF_TONE: Record<string, string> = { movimentacao: "bb-amber", email: "bb-violet", atribuicao: "bb-brand", revisao: "bb-violet", devolucao: "bb-amber", intimacao: "bb-brand", fatal24h: "bb-red", mencao: "bb-violet", lembrete: "bb-amber" };

interface Route { pattern: string; section: string; group?: string; base?: string; render: (p: Record<string, string>) => ReactNode }

const ROUTES: Route[] = [
  { pattern: "/", section: "Início", render: () => <PanelPage /> },
  { pattern: "/pessoas", group: "Jurídico", section: "Pessoas", render: () => <PeoplePage /> },
  { pattern: "/pessoas/:id", group: "Jurídico", section: "Pessoas", base: "/pessoas", render: (p) => <PersonDetail id={p.id!} /> },
  { pattern: "/casos", group: "Jurídico", section: "Casos", render: () => <CasesPage /> },
  { pattern: "/casos/:id", group: "Jurídico", section: "Casos", base: "/casos", render: (p) => <CaseDetail id={p.id!} /> },
  { pattern: "/processos", group: "Jurídico", section: "Processos", render: () => <ProcessesPage /> },
  { pattern: "/processos/:id", group: "Jurídico", section: "Processos", base: "/processos", render: (p) => <ProcessDetail id={p.id!} /> },
  { pattern: "/atividades", section: "Atividades", render: () => <ActivitiesPage /> },
  { pattern: "/atividades/:id", section: "Atividades", base: "/atividades", render: (p) => <ActivityDetailPage id={p.id!} /> },
  { pattern: "/agenda", section: "Agenda", render: () => <AgendaPage /> },
  { pattern: "/controladoria", group: "Jurídico", section: "Controladoria", render: () => <ControlPage /> },
  { pattern: "/controladoria/intimacoes/:id", group: "Jurídico", section: "Controladoria", base: "/controladoria", render: (p) => <CommWorkspace id={p.id!} /> },
  { pattern: "/financeiro", group: "Gestão", section: "Financeiro", render: () => <FinancePage /> },
  { pattern: "/indicadores", group: "Gestão", section: "Indicadores", render: () => <IndicatorsPage /> },
  { pattern: "/sociedade", group: "Gestão", section: "Sociedade", render: () => <SocietyPage /> },
  { pattern: "/fluxos", group: "Administração", section: "BPM / Workflow", render: () => <WorkflowPage /> },
  { pattern: "/documentos", group: "Jurídico", section: "Documentos", render: () => <DocumentsPage /> },
  { pattern: "/honorarios", group: "Jurídico", section: "Honorários", render: () => <FeesPage /> },
  { pattern: "/configuracoes", group: "Administração", section: "Configurações", render: () => <SettingsPage /> },
  { pattern: "/conta", section: "Minha conta", render: () => <AccountPage /> },
  { pattern: "/mural", section: "Mural", render: () => <MuralPage /> },
  { pattern: "/chat", section: "Chat", render: () => <ChatPage /> },
  { pattern: "/assistente", section: "Assistente", render: () => <AssistantPage /> },
];

function NavLink({ to, icon, children, badge, soft }: { to: string; icon: string; children: ReactNode; badge?: number; soft?: boolean }): JSX.Element {
  const { path } = useLocation();
  const active = to === "/" ? path === "/" : path === to || path.startsWith(`${to}/`);
  return (
    <Link to={to} className={active ? "active" : ""} data-label={typeof children === "string" ? children : undefined}>
      <Icon name={icon} />
      <span className="nav-label">{children}</span>
      {badge ? <span className={`nav-badge ${soft ? "soft" : ""}`}>{badge}</span> : null}
    </Link>
  );
}

interface PanelLite {
  counts: { mineOverdue: number; reviewQueue: number };
  controladoria: { pending: number; captureOk: boolean } | null;
  alerts: Array<{ level: "vermelho" | "amarelo"; text: string; link: string }>;
}

/** Fecha o menu suspenso ao clicar fora. */
function useOutside(ref: React.RefObject<HTMLElement>, onOut: () => void): void {
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onOut(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, onOut]);
}

/** Seletor de unidade do menu lateral (filtra a Visão do sócio e os Indicadores). */
function UnitSwitcher({ myUnit }: { myUnit: { id: string; name: string } | null }): JSX.Element {
  const { catalog } = useLookups();
  const unitId = useUnitStore((s) => s.unitId);
  const setUnit = useUnitStore((s) => s.set);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutside(ref, () => setOpen(false));
  const units = (catalog?.units ?? []).filter((u) => u.active);
  const current = units.find((u) => u.id === unitId);
  const label = current?.name ?? (units.length > 1 ? "Todas as unidades" : myUnit?.name ?? units[0]?.name ?? "Escritório");
  return (
    <div className="unit-switch" ref={ref}>
      <button className="unit-btn" onClick={() => units.length > 1 && setOpen(!open)} title={units.length > 1 ? "Trocar a unidade exibida" : undefined}>
        <Icon name="building" />
        <span className="grow"><small>Unidade</small><b>{label}</b></span>
        {units.length > 1 && <Icon name="chevdown" size="sm" />}
      </button>
      {open && (
        <div className="unit-pop">
          <div className="unit-note">Filtra Indicadores, Financeiro › Resumo, Agenda (geral) e Atividades (Todas e Fila).</div>
          <button className={!unitId ? "on" : ""} onClick={() => { setUnit(""); setOpen(false); }}>Todas as unidades</button>
          {units.map((u) => <button key={u.id} className={u.id === unitId ? "on" : ""} onClick={() => { setUnit(u.id); setOpen(false); }}>{u.name}{u.id === myUnit?.id ? <small> · minha</small> : null}</button>)}
        </div>
      )}
    </div>
  );
}

export function Shell(): JSX.Element {
  const [calcAct, setCalcAct] = useState<{ processId: string | null; caseId: string | null; apply: CalcApply } | null>(null);
  const { path } = useLocation();
  const pageTitle = usePageTitle();
  const clear = useAuth((s) => s.clear);
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { data: prof } = useProfile();
  const { can, me: cme } = useLookups();
  usePrefsLoader(cme?.id);
  const [dark, setDarkState] = useState(isDark());
  const [mini, setMini] = useState(() => { try { return localStorage.getItem("vg.menu.recolhido") === "1"; } catch { return false; } });
  const toggleMini = () => { const n = !mini; setMini(n); try { localStorage.setItem("vg.menu.recolhido", n ? "1" : "0"); } catch { /* sem storage */ } };
  const { data: brand } = useBrand();
  const unitId = useUnitStore((s) => s.unitId);
  const [cmdk, setCmdk] = useState(false);
  const [bell, setBell] = useState(false);
  const [help, setHelp] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [menu, setMenu] = useState(false);
  const [tip, setTip] = useState<{ text: string; top: number; left: number } | null>(null);
  const feats = useFeatures();
  useEffect(() => { setMenu(false); setUserMenu(false); setHelp(false); setTip(null); }, [path]);
  const bellRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  useOutside(bellRef, () => setBell(false));
  useOutside(helpRef, () => setHelp(false));
  useOutside(userRef, () => setUserMenu(false));

  const { data: theme } = useQuery({ queryKey: ["tema"], queryFn: () => api.get<OfficeTheme>("/config/tema"), staleTime: 300_000 });
  useEffect(() => { applyTheme(theme); }, [theme, dark]);

  const { data: notif } = useQuery({
    queryKey: ["notificacoes"], refetchInterval: 60_000,
    queryFn: () => api.get<{ unread: number; items: Array<{ id: string; kind: string; title: string; body: string | null; link: string | null; readAt: string | null; createdAt: string }> }>("/notificacoes"),
  });
  const { data: panel } = useQuery({ queryKey: ["painel", false], queryFn: () => api.get<PanelLite>("/painel"), refetchInterval: 120_000 });
  useLiveChat();
  const { data: chatSum } = useQuery({ queryKey: ["chat-resumo"], queryFn: () => api.get<{ unread: number; mentions: number }>("/chat/resumo"), refetchInterval: 120_000 });

  // Atalhos: ⌘K / Ctrl+K abre a busca global.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setCmdk(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const logout = async (): Promise<void> => {
    try { await api.post("/auth/logout"); } finally { qc.clear(); clear(); window.history.replaceState(null, "", "/"); }
  };

  let page: ReactNode = <div className="card">Página não encontrada. <Link to="/">Voltar ao início</Link></div>;
  let route: Route | undefined;
  for (const r of ROUTES) {
    const m = match(r.pattern, path);
    if (m) { page = r.render(m); route = r; break; }
  }
  const alerts = panel?.alerts ?? [];
  const markRead = async (ids?: string[]) => { await api.post("/notificacoes/lidas", ids ? { ids } : {}); await qc.invalidateQueries({ queryKey: ["notificacoes"] }); };
  const officeName = theme?.officeName || me?.tenant.displayName || "";
  useEffect(() => { setTitleSuffix(officeName); }, [officeName]);
  useEffect(() => { setFavicon(brand?.favicon); }, [brand?.favicon]);
  const icon = pickIcon(brand, unitId || prof?.unit?.id);
  const onNavHover = (e: React.MouseEvent) => {
    if (!mini) return;
    const a = (e.target as HTMLElement).closest<HTMLElement>("a[data-label]");
    if (!a) { setTip(null); return; }
    const r = a.getBoundingClientRect();
    setTip({ text: a.dataset.label ?? "", top: r.top + r.height / 2, left: r.right + 10 });
  };
  useEffect(() => { if (!mini) setTip(null); }, [mini]);
  const captureOk = panel?.controladoria ? panel.controladoria.captureOk : true;
  const toggleDark = () => { setDark(!dark); setDarkState(!dark); };

  return (
    <div className={`layout ${menu ? "menu-open" : ""} ${mini ? "side-min" : ""}`}>
      {menu && <div className="menu-backdrop" onClick={() => setMenu(false)} />}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className={`logo ${icon ? "img" : ""}`}>{icon ? <img src={icon} alt={officeName || "Logo"} /> : <Icon name="scale" />}</div>
          <div className="brand-text">
            <div className="name">{officeName || "VG APP"}</div>
            <div className="tenant">Sistema jurídico</div>
          </div>
          <button className="side-toggle" onClick={toggleMini} title={mini ? "Expandir o menu" : "Recolher o menu"} aria-label={mini ? "Expandir o menu" : "Recolher o menu"}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{mini ? <path d="M9 6l6 6-6 6" /> : <path d="M15 6l-6 6 6 6" />}</svg>
          </button>
        </div>
        <UnitSwitcher myUnit={prof?.unit ?? null} />
        <nav className="nav" onMouseOver={onNavHover} onMouseLeave={() => setTip(null)}>
          <NavLink to="/" icon="home">Início</NavLink>
          <NavLink to="/atividades" icon="check" badge={(panel?.counts.mineOverdue ?? 0) + (panel?.counts.reviewQueue ?? 0)}>Atividades</NavLink>
          <NavLink to="/agenda" icon="cal">Agenda</NavLink>
          <NavLink to="/chat" icon="chat" badge={chatSum?.unread}>Chat</NavLink>
          <NavLink to="/mural" icon="pin">Mural</NavLink>
          {(can("ia.assistente") || can("config.gerenciar")) && <NavLink to="/assistente" icon="bot">Assistente</NavLink>}
          {(can("pessoa.ver") || can("caso.ver") || can("processo.ver") || can("comunicacao.ver") || can("documento.ver")) && <div className="nav-section">Jurídico</div>}
          {can("pessoa.ver") && <NavLink to="/pessoas" icon="user">Pessoas</NavLink>}
          {can("caso.ver") && <NavLink to="/casos" icon="folder">Casos</NavLink>}
          {can("processo.ver") && <NavLink to="/processos" icon="scale">Processos</NavLink>}
          {can("comunicacao.ver") && <NavLink to="/controladoria" icon="radar" badge={panel?.controladoria?.pending} soft>Controladoria</NavLink>}
          {can("documento.ver") && <NavLink to="/documentos" icon="doc">Documentos</NavLink>}
          {(can("caso.ver") || can("financeiro.ver")) && <NavLink to="/honorarios" icon="cash">Honorários</NavLink>}
          {((can("bi.ver") && feats.on("indicadores")) || can("financeiro.ver") || (can("sociedade.ver") && feats.on("sociedade"))) && <div className="nav-section">Gestão</div>}
          {can("bi.ver") && feats.on("indicadores") && <NavLink to="/indicadores" icon="chart">Indicadores</NavLink>}
          {can("financeiro.ver") && <NavLink to="/financeiro" icon="coin">Financeiro</NavLink>}
          {can("sociedade.ver") && feats.on("sociedade") && <NavLink to="/sociedade" icon="flag">Sociedade</NavLink>}
          {(can("config.gerenciar") || can("permissao.gerenciar") || can("auditoria.ver")) && <div className="nav-section">Administração</div>}
          <NavLink to="/fluxos" icon="flow">{can("config.gerenciar") ? "BPM / Workflow" : "Fluxos"}</NavLink>
          {(can("config.gerenciar") || can("permissao.gerenciar") || can("auditoria.ver")) && <NavLink to="/configuracoes" icon="gear">Configurações</NavLink>}
        </nav>
        <div className="sidebar-foot">
          <button className="org-btn" onClick={() => can("config.gerenciar") && navigate("/configuracoes?aba=aparencia")} title={can("config.gerenciar") ? "Aparência e dados do escritório" : undefined}>
            <span className="grow"><small>Organização</small><b>{officeName}</b></span>
            {can("config.gerenciar") && <Icon name="chev" size="sm" />}
          </button>
        </div>
      </aside>
      <div className="content">
        <header className="topbar2">
          <button className="iconbtn menu-btn" title="Menu" aria-label="Abrir menu" onClick={() => setMenu(!menu)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div className="gsearch" onClick={() => setCmdk(true)} role="button">
            <Icon name="search" size="sm" /><span className="lbl">Buscar pessoa, caso, processo, atividade…</span><span className="kbd">⌘K</span>
          </div>
          <span className="spacer" />
          <div ref={bellRef} style={{ position: "relative" }}>
            <button className="iconbtn" title="Avisos" onClick={() => setBell(!bell)}>
              <Icon name="bell" />{(alerts.length + (notif?.unread ?? 0)) > 0 && <span className="dotn">{alerts.length + (notif?.unread ?? 0)}</span>}
            </button>
            {bell && (
              <div className="dropdown notif-dd">
                <div className="dh row between">
                  <span>Avisos</span>
                  {(notif?.unread ?? 0) > 0 && <button className="link-btn small" onClick={() => void markRead()}>marcar todos como lidos</button>}
                </div>
                {alerts.map((a, i) => (
                  <div key={`a${i}`} className="di" onClick={() => { setBell(false); navigate(a.link); }}>
                    <span className={`bubble ${a.level === "vermelho" ? "bb-red" : "bb-amber"}`}><Icon name={a.level === "vermelho" ? "alert" : "clock"} /></span>
                    <span>{a.text}</span>
                  </div>
                ))}
                {(notif?.items ?? []).map((n) => (
                  <div key={n.id} className={`di ${n.readAt ? "read" : ""}`} onClick={() => { setBell(false); void markRead([n.id]); if (n.link) navigate(n.link); }}>
                    <span className={`bubble ${NOTIF_TONE[n.kind] ?? "bb-brand"}`}><Icon name={NOTIF_ICON[n.kind] ?? "bell"} /></span>
                    <span className="grow"><span style={{ fontWeight: n.readAt ? 400 : 600 }}>{n.title}</span>{n.body && <div className="muted small">{n.body}</div>}<div className="muted small">{fmtDateTime(n.createdAt)}</div></span>
                  </div>
                ))}
                {!alerts.length && !(notif?.items.length) && <div className="de">Nada pendente. 👌</div>}
              </div>
            )}
          </div>
          <div ref={helpRef} style={{ position: "relative" }}>
            <button className="iconbtn" title="Ajuda" onClick={() => setHelp(!help)}><Icon name="help" /></button>
            {help && (
              <div className="dropdown help-dd">
                <div className="dh">Ajuda rápida</div>
                <div className="di" onClick={() => { setHelp(false); setCmdk(true); }}><span className="kbd">⌘K</span><span>Buscar qualquer coisa (Ctrl+K no Windows)</span></div>
                <div className="di" onClick={() => { setHelp(false); navigate("/atividades?nova=1"); }}><Icon name="plus" size="sm" /><span>Registrar uma atividade</span></div>
                <div className="di" onClick={() => { setHelp(false); navigate("/agenda"); }}><Icon name="cal" size="sm" /><span>Ver a agenda e os prazos</span></div>
                <div className="de small">Dúvidas sobre o sistema? Fale com o administrador do escritório.</div>
              </div>
            )}
          </div>
          <div ref={userRef} style={{ position: "relative" }}>
            <button className="user-chip" onClick={() => setUserMenu(!userMenu)}>
              {me && <Avatar name={me.identity.displayName} id={cme?.id} size={34} />}
              <span className="uc-text"><b>{me?.identity.displayName}</b><small>{prof?.primaryRole?.name ?? ""}</small></span>
              <Icon name="chevdown" size="sm" />
            </button>
            {userMenu && (
              <div className="dropdown user-dd">
                <div className="di" onClick={() => navigate("/conta")}><Icon name="user" size="sm" /><span>Minha conta</span></div>
                <div className="di" onClick={toggleDark}><Icon name={dark ? "sun" : "moon"} size="sm" /><span>{dark ? "Modo claro" : "Modo escuro"}</span></div>
                <div className="di" onClick={() => void logout()}><Icon name="logout" size="sm" /><span>Sair</span></div>
              </div>
            )}
          </div>
        </header>
        {route && route.pattern !== "/" && (
          <div className="crumbs-bar">
            <Link to="/">Início</Link>
            {route.group && <><Icon name="chev" size="sm" /><span>{route.group}</span></>}
            <Icon name="chev" size="sm" />
            {route.base ? <Link to={route.base}>{route.section}</Link> : <b>{route.section}</b>}
            {route.base && pageTitle && <><Icon name="chev" size="sm" /><b>{pageTitle}</b></>}
          </div>
        )}
        <main className="main"><ErrorBoundary resetKey={location.pathname + location.search}>{page}</ErrorBoundary></main>
        <footer className="appfoot">
          <span>{officeName || "VG APP"}</span>
          <span className="sep">|</span>
          <span className="muted">Pessoa → Caso → Processo</span>
          <span className="spacer" />
          <span className={`status ${captureOk ? "ok" : "bad"}`} onClick={() => !captureOk && navigate("/controladoria")}>
            <i />{captureOk ? "Todos os sistemas operacionais" : "Captura de publicações com falha"}
          </span>
          <span className="muted">{new Date().toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" })}</span>
        </footer>
      </div>
      {tip && <div className="side-tip" style={{ top: tip.top, left: tip.left }}>{tip.text}</div>}
      {cmdk && <CommandPalette onClose={() => setCmdk(false)} />}
      <DeadlineFab onCreateActivity={(p) => setCalcAct(p)} />
      {calcAct && <NewActivityModal onClose={() => setCalcAct(null)}
        defaults={{ ...(calcAct.processId ? { ctx: { kind: "processo", id: calcAct.processId, label: "Este processo" } } : calcAct.caseId ? { ctx: { kind: "caso", id: calcAct.caseId, label: "Este caso" } } : {}),
          fatalDueAt: calcAct.apply.fatalDueAt, internalDueAt: calcAct.apply.internalDueAt, description: calcAct.apply.note }} />}
      <ChatDock />
      <Toaster />
      <ConfirmHost />
    </div>
  );
}

// ------------------------------------------------------------ busca global

interface Hit { kind: "pessoa" | "caso" | "processo" | "atividade" | "honorario"; id: string; label: string; sub: string; link: string }

function CommandPalette({ onClose }: { onClose: () => void }): JSX.Element {
  const [q, setQ] = useState("");
  const [deb, setDeb] = useState("");
  const [sel, setSel] = useState(0);
  const { can } = useLookups();
  useEffect(() => { const t = setTimeout(() => setDeb(q.trim()), 200); return () => clearTimeout(t); }, [q]);
  const { data: hits = [], isFetching } = useQuery({
    queryKey: ["busca", deb],
    enabled: deb.length >= 2,
    // Busca com contexto (nome, CPF/CNPJ, nº do caso, CNJ, nº da atividade).
    queryFn: async (): Promise<Hit[]> => (await api.get<Array<{ kind: Hit["kind"]; id: string; label: string; context: string; link: string }>>(`/busca${qs({ q: deb, limite: 6 })}`))
      .map((h) => ({ kind: h.kind, id: h.id, label: h.label, sub: h.context, link: h.link })),
  });
  const go = (h: Hit) => { onClose(); navigate(h.link); };
  const icon: Record<Hit["kind"], string> = { pessoa: "user", caso: "folder", processo: "scale", atividade: "check", honorario: "coin" };
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-body">
          <input className="input cmdk-input" autoFocus placeholder="Buscar pessoa, CPF, processo (CNJ), caso, atividade ou honorários…" value={q}
            onChange={(e) => { setQ(e.target.value); setSel(0); }}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
              if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, hits.length - 1)); }
              if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
              if (e.key === "Enter" && hits[sel]) go(hits[sel]!);
            }} />
          <div className="cmdk-list">
            {hits.map((h, i) => (
              <div key={`${h.kind}${h.id}`} className={`cmdk-item ${i === sel ? "sel" : ""}`} onMouseEnter={() => setSel(i)} onClick={() => go(h)}>
                <Icon name={icon[h.kind]} size="sm" />
                <span><span className={h.kind === "processo" ? "mono" : ""}>{h.label}</span>{h.sub && <span className="muted small"> · {h.sub}</span>}</span>
                <span className="k">{h.kind}</span>
              </div>
            ))}
            {deb.length >= 2 && !isFetching && !hits.length && <div className="muted small pad">Nada encontrado.</div>}
            {deb.length < 2 && <div className="muted small pad">Digite ao menos 2 letras. Use ↑ ↓ e Enter.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
